import { isDrawingFormCategory, TicketingFormCategory } from "@simplyk/common";

import { toFiniteNumber } from "@/common/helpers/toFiniteNumber";
import { RateData } from "@/features/LiveFormEditor/Steps/StepTickets/Rates/types";

export interface RateStatusChipInput {
  soldCount: number;
  seats: RateData["seats"] | string;
  isGroup: boolean;
  formCategory: TicketingFormCategory;
  attendeesByTicket: RateData["attendeesByTicket"] | string;
  hasMultipleDates: boolean;
  scheduleStartDate: Date | string | null;
  scheduleEndDate: Date | string | null;
  now: Date;
}

export interface RateStatusChip {
  key:
    | "ticketing.rateStatusChip.sold"
    | "ticketing.rateStatusChip.soldOfCapacity"
    | "ticketing.rateStatusChip.groupsSold"
    | "ticketing.rateStatusChip.groupsSoldOfCapacity"
    | "ticketing.rateStatusChip.bundlesSold"
    | "ticketing.rateStatusChip.bundlesSoldOfCapacity"
    | "ticketing.rateStatusChip.members"
    | "ticketing.rateStatusChip.endedSold"
    | "ticketing.rateStatusChip.endedSoldOfCapacity"
    | "ticketing.rateStatusChip.endedGroupsSold"
    | "ticketing.rateStatusChip.endedGroupsSoldOfCapacity"
    | "ticketing.rateStatusChip.endedBundlesSold"
    | "ticketing.rateStatusChip.endedBundlesSoldOfCapacity"
    | "ticketing.rateStatusChip.endedMembers"
    | "ticketing.rateStatusChip.notStartedSold"
    | "ticketing.rateStatusChip.notStartedSoldOfCapacity"
    | "ticketing.rateStatusChip.notStartedGroupsSold"
    | "ticketing.rateStatusChip.notStartedGroupsSoldOfCapacity"
    | "ticketing.rateStatusChip.notStartedBundlesSold"
    | "ticketing.rateStatusChip.notStartedBundlesSoldOfCapacity"
    | "ticketing.rateStatusChip.notStartedMembers";
  params: { count: number; capacity?: number };
  vibe: "neutral" | "positive" | "danger";
  dotVibe: "positive" | "warning" | "danger";
  withCheckIcon: boolean;
}

export const buildRateStatusChip = ({
  soldCount,
  seats,
  isGroup,
  formCategory,
  attendeesByTicket,
  hasMultipleDates,
  scheduleStartDate,
  scheduleEndDate,
  now,
}: RateStatusChipInput): RateStatusChip => {
  const isDrawing = isDrawingFormCategory(formCategory);
  const attendees = isGroup ? toFiniteNumber(attendeesByTicket) || 1 : 1;
  const capacity = toFiniteNumber(seats);
  const soldUnits = Math.floor(soldCount / attendees);

  const hasCapacity = capacity !== null && capacity >= 0 && !hasMultipleDates;
  const params: RateStatusChip["params"] = hasCapacity ? { count: soldUnits, capacity } : { count: soldUnits };

  const hasEnded = Boolean(scheduleEndDate) && new Date(scheduleEndDate as Date).getTime() <= now.getTime();
  const hasNotStarted =
    !hasEnded && Boolean(scheduleStartDate) && new Date(scheduleStartDate as Date).getTime() > now.getTime();

  if (formCategory === TicketingFormCategory.MembershipV2) {
    return {
      key: hasEnded
        ? "ticketing.rateStatusChip.endedMembers"
        : hasNotStarted
          ? "ticketing.rateStatusChip.notStartedMembers"
          : "ticketing.rateStatusChip.members",
      params: { count: soldUnits },
      vibe: hasEnded ? "danger" : "neutral",
      dotVibe: hasEnded ? "danger" : hasNotStarted ? "warning" : "positive",
      withCheckIcon: false,
    };
  }

  const keyContext = { isGroup, isDrawing, hasCapacity };
  const isSoldOut = hasCapacity && soldUnits >= capacity;

  if (hasNotStarted) {
    return {
      key: pickNotStartedKey(keyContext),
      params,
      vibe: "neutral",
      dotVibe: "warning",
      withCheckIcon: false,
    };
  }

  if (hasEnded) {
    return { key: pickEndedKey(keyContext), params, vibe: "danger", dotVibe: "danger", withCheckIcon: false };
  }

  return {
    key: pickSellingKey(keyContext),
    params,
    vibe: isSoldOut ? "positive" : "neutral",
    dotVibe: "positive",
    withCheckIcon: isSoldOut,
  };
};

interface ChipKeyContext {
  isGroup: boolean;
  isDrawing: boolean;
  hasCapacity: boolean;
}

const pickNotStartedKey = ({ isGroup, isDrawing, hasCapacity }: ChipKeyContext): RateStatusChip["key"] => {
  if (!isGroup) {
    return hasCapacity
      ? "ticketing.rateStatusChip.notStartedSoldOfCapacity"
      : "ticketing.rateStatusChip.notStartedSold";
  }
  if (isDrawing) {
    return hasCapacity
      ? "ticketing.rateStatusChip.notStartedBundlesSoldOfCapacity"
      : "ticketing.rateStatusChip.notStartedBundlesSold";
  }
  return hasCapacity
    ? "ticketing.rateStatusChip.notStartedGroupsSoldOfCapacity"
    : "ticketing.rateStatusChip.notStartedGroupsSold";
};

const pickEndedKey = ({ isGroup, isDrawing, hasCapacity }: ChipKeyContext): RateStatusChip["key"] => {
  if (!isGroup) {
    return hasCapacity ? "ticketing.rateStatusChip.endedSoldOfCapacity" : "ticketing.rateStatusChip.endedSold";
  }
  if (isDrawing) {
    return hasCapacity
      ? "ticketing.rateStatusChip.endedBundlesSoldOfCapacity"
      : "ticketing.rateStatusChip.endedBundlesSold";
  }
  return hasCapacity
    ? "ticketing.rateStatusChip.endedGroupsSoldOfCapacity"
    : "ticketing.rateStatusChip.endedGroupsSold";
};

const pickSellingKey = ({ isGroup, isDrawing, hasCapacity }: ChipKeyContext): RateStatusChip["key"] => {
  if (!isGroup) {
    return hasCapacity ? "ticketing.rateStatusChip.soldOfCapacity" : "ticketing.rateStatusChip.sold";
  }
  if (isDrawing) {
    return hasCapacity ? "ticketing.rateStatusChip.bundlesSoldOfCapacity" : "ticketing.rateStatusChip.bundlesSold";
  }
  return hasCapacity ? "ticketing.rateStatusChip.groupsSoldOfCapacity" : "ticketing.rateStatusChip.groupsSold";
};
